/**
 * Molecule — stable section reveals
 * Pattern: hide once → ScrollTrigger → fade/rise to visible (no fromTo flash)
 */
(function () {
  'use strict';

  var SKIP =
    /section-molecule-(header|footer|announcement|form-header)(\s|$)|section-wm-form-header(\s|$)|section-wm-assessment(\s|$)|section-wm-ed-assessment(\s|$)/;
  var EASE = 'power2.out';
  var START = 'top 80%';
  var DURATION = 0.7;

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function whenReady(fn) {
    function run() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        window.setTimeout(run, 40);
        return;
      }
      gsap.registerPlugin(ScrollTrigger);
      fn();
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  function qsa(root, sel) {
    try {
      return Array.prototype.slice.call(root.querySelectorAll(sel));
    } catch (e) {
      return [];
    }
  }

  function unique(nodes) {
    return nodes.filter(function (el, i, arr) {
      return el && arr.indexOf(el) === i;
    });
  }

  function visible(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute('hidden')) return false;
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function onlyVisible(nodes) {
    return (Array.isArray(nodes) ? nodes : []).filter(visible);
  }

  function sectionRoot(sectionEl) {
    return (
      sectionEl.querySelector(':scope > section') ||
      sectionEl.querySelector('[class^="wm-"]') ||
      sectionEl.firstElementChild
    );
  }

  var CARD_SEL =
    '.wm-why__card, .wm-ppc-why__card, .wm-team__card, .wm-pg__card, .wm-cp__step, .wm-accordion__item, .wm-sbc__card, .wm-sbc__item, .wm-bs__card, .wm-card-product, .wm-tx__card, .wm-tx__item, .wm-blog__card, .wm-card-blog, .wm-stories__card, .wm-stories__slide, .wm-tl__item, .wm-steps__item, .wm-step, .wm-hiw__step, .wm-hiw__card, .wm-results__card, .wm-results__item, .wm-results__refs-item, .wm-tpr__card, .wm-tpr__item, .wm-st__card, .wm-st__item, .wm-ph__card, .wm-bmi__card, .wm-cta__card, .wm-select__card';

  function isInsideCard(el) {
    return !!(el && el.closest(CARD_SEL + ', .wm-hero__card'));
  }

  function isButton(el) {
    return !!(el && el.matches && el.matches('a.wm-btn, button.wm-btn, [class*="__btn"]'));
  }

  function hide(els) {
    var list = onlyVisible(Array.isArray(els) ? els : [els]);
    if (!list.length) return list;
    gsap.set(list, { opacity: 0, y: 24 });
    return list;
  }

  function reveal(tl, els, position, opts) {
    var list = onlyVisible(Array.isArray(els) ? els : [els]);
    if (!list.length) return;
    opts = opts || {};
    tl.to(
      list,
      {
        opacity: 1,
        y: 0,
        duration: opts.duration || DURATION,
        stagger: opts.stagger || 0,
        ease: EASE,
      },
      position
    );
  }

  function revealRiseOnly(tl, els, position) {
    var list = onlyVisible(Array.isArray(els) ? els : [els]);
    if (!list.length) return;
    gsap.set(list, { y: 18 });
    tl.to(list, { y: 0, duration: 0.55, ease: EASE, stagger: 0.05 }, position);
  }

  /* Collectors */
  function collectHeadings(root) {
    return onlyVisible(
      unique(
        []
          .concat(qsa(root, 'h1, h2, h3'))
          .concat(qsa(root, '.wm-h1, .wm-h2, .wm-h3'))
          .concat(qsa(root, '[class*="__heading"]'))
      ).filter(function (el) {
        return !isInsideCard(el);
      })
    );
  }

  function collectSupport(root, headings) {
    return onlyVisible(
      unique(
        []
          .concat(qsa(root, '[class*="__eyebrow"]'))
          .concat(qsa(root, '[class*="__text"]'))
          .concat(qsa(root, '[class*="__sub"]'))
          .concat(qsa(root, '[class*="__desc"]'))
      ).filter(function (el) {
        if (isButton(el) || isInsideCard(el)) return false;
        if (el.closest('form')) return false;
        if (/--mobile$/.test(el.className || '')) return false;
        if (
          headings.some(function (h) {
            return h === el || h.contains(el);
          })
        ) {
          return false;
        }
        return true;
      })
    );
  }

  function collectButtons(root) {
    return onlyVisible(
      unique(
        [].concat(qsa(root, 'a.wm-btn, button.wm-btn')).concat(qsa(root, '[class*="__btn"]'))
      ).filter(function (el) {
        if (el.closest('form')) return false;
        if (/--mobile$/.test(el.className || '')) return false;
        return true;
      })
    );
  }

  function collectCards(root) {
    var list = unique(qsa(root, CARD_SEL));
    return onlyVisible(
      list.filter(function (el) {
        return !list.some(function (other) {
          return other !== el && other.contains(el);
        });
      })
    );
  }

  function collectVisual(root) {
    // Intentionally excludes .wm-bc__media (banner backgrounds stay static)
    return root.querySelector(
      '.wm-fp__visual, .wm-it__visual, .wm-it__media, .wm-cta__media, .wm-ph__media, .wm-bmi__media, .wm-results__media, .wm-htu__media'
    );
  }

  function makeScrollTimeline(sectionEl, idSuffix) {
    return gsap.timeline({
      defaults: { ease: EASE },
      scrollTrigger: {
        trigger: sectionEl,
        start: START,
        once: true,
        toggleActions: 'play none none none',
        id: (sectionEl.id || 'wm') + '-' + idSuffix,
      },
    });
  }

  /* —— Heroes (load, not scroll) —— */
  function animateHero(root) {
    var trust = root.querySelector('.wm-hero__trustpilot');
    var heading = root.querySelector('.wm-hero__heading, h1, h2');
    var text = root.querySelector('.wm-hero__text');
    var cards = onlyVisible(qsa(root, '.wm-hero__card'));
    var targets = hide([trust, heading, text].concat(cards).filter(Boolean));
    if (!targets.length) return;

    var tl = gsap.timeline({ defaults: { ease: EASE } });
    if (trust) reveal(tl, trust, 0, { duration: 0.5 });
    if (heading) reveal(tl, heading, 0.05);
    if (text) reveal(tl, text, 0.12, { duration: 0.55 });
    if (cards.length) {
      gsap.set(cards, { opacity: 1, y: 32 });
      tl.to(cards, { y: 0, duration: 0.55, stagger: 0.1, ease: EASE }, 0.18);
    }
  }

  function animatePpcHero(root) {
    var heading = root.querySelector('.wm-ppc-hero__heading, h1, h2');
    var buttons = collectButtons(root);
    var rest = onlyVisible(
      qsa(root, '.wm-ppc-hero__price, .wm-ppc-hero__text').filter(function (el) {
        return el !== heading && !isButton(el);
      })
    );
    hide([heading].concat(rest).filter(Boolean));
    if (buttons.length) gsap.set(buttons, { y: 16 });

    var tl = gsap.timeline({ defaults: { ease: EASE } });
    if (heading) reveal(tl, heading, 0);
    if (rest.length) reveal(tl, rest, 0.1, { stagger: 0.06, duration: 0.55 });
    if (buttons.length) tl.to(buttons, { y: 0, duration: 0.5, stagger: 0.05, ease: EASE }, 0.18);
  }

  /* —— Begin consultation —— */
  function animateBeginConsultation(sectionEl, root) {
    // Keep background static
    gsap.set(qsa(root, '.wm-bc__media, .wm-bc__img, .wm-bc__overlay'), {
      clearProps: 'opacity,transform,visibility',
    });

    var heading = root.querySelector('.wm-bc__heading');
    var text = root.querySelector('.wm-bc__text');
    var items = onlyVisible(qsa(root, '.wm-bc__item'));
    var btn = root.querySelector('.wm-bc__btn');

    hide([heading, text].concat(items).filter(Boolean));
    if (btn && visible(btn)) gsap.set(btn, { opacity: 0, y: 18 });

    var tl = makeScrollTimeline(sectionEl, 'bc');
    if (heading) reveal(tl, heading, 0, { duration: 0.8 });
    if (text) reveal(tl, text, 0.1, { duration: 0.65 });
    if (items.length) reveal(tl, items, 0.22, { stagger: 0.09, duration: 0.65 });
    if (btn && visible(btn)) reveal(tl, btn, items.length ? '-=0.25' : 0.35, { duration: 0.6 });
  }

  /* —— Why choose —— */
  function animatePpcWhy(sectionEl, root) {
    var heading = root.querySelector('.wm-ppc-why__heading');
    var text = root.querySelector('.wm-ppc-why__text');
    var buttons = collectButtons(root);
    var cards = onlyVisible(qsa(root, '.wm-ppc-why__card'));

    hide([heading, text].concat(cards).filter(Boolean));
    if (buttons.length) gsap.set(buttons, { y: 14 });

    var tl = makeScrollTimeline(sectionEl, 'ppc-why');
    if (heading) reveal(tl, heading, 0, { duration: 0.75 });
    if (text) reveal(tl, text, 0.08, { duration: 0.55 });
    if (buttons.length) tl.to(buttons, { y: 0, duration: 0.5, ease: EASE }, 0.12);
    if (cards.length) reveal(tl, cards, 0.16, { stagger: 0.08, duration: 0.75 });
  }

  /* —— Generic scroll section —— */
  function animateScrollSection(sectionEl, root) {
    var headings = collectHeadings(root);
    var support = collectSupport(root, headings);
    var buttons = collectButtons(root);
    var visual = collectVisual(root);
    var cards = collectCards(root);

    // Never animate full-bleed banner media accidentally
    if (visual && visual.closest('.wm-bc__media')) visual = null;

    var toHide = [].concat(headings, support, cards);
    if (visual) toHide.push(visual);
    hide(toHide);
    if (buttons.length) gsap.set(buttons, { y: 16 });

    if (!headings.length && !support.length && !cards.length && !visual && !buttons.length) {
      var kids = onlyVisible(Array.prototype.slice.call(root.children)).filter(function (el) {
        return !el.classList.contains('wm-bc__media') && !el.matches('[class*="__media"]');
      });
      if (!kids.length) return;
      hide(kids);
      var fb = makeScrollTimeline(sectionEl, 'fallback');
      reveal(fb, kids, 0, { stagger: 0.08 });
      return;
    }

    var tl = makeScrollTimeline(sectionEl, 'reveal');
    var t = 0;
    if (headings.length) {
      reveal(tl, headings, t, { stagger: 0.06, duration: 0.75 });
      t += 0.1;
    }
    if (support.length) {
      reveal(tl, support, t, { stagger: 0.05, duration: 0.55 });
      t += 0.08;
    }
    if (buttons.length) {
      tl.to(buttons, { y: 0, duration: 0.5, stagger: 0.04, ease: EASE }, t);
      t += 0.06;
    }
    if (visual && visible(visual)) {
      reveal(tl, visual, t, { duration: 0.7 });
      t += 0.08;
    }
    if (cards.length) {
      reveal(tl, cards, t, { stagger: 0.08, duration: 0.7 });
    }
  }

  function animateImageText(sectionEl, root) {
    var eyebrow = root.querySelector('.wm-it__eyebrow');
    var heading = root.querySelector('.wm-it__heading');
    var text = root.querySelector('.wm-it__text');
    var buttons = onlyVisible(qsa(root, '.wm-it__btn'));
    var actions = root.querySelector('.button_main');
    var media = root.querySelector('.wm-it__visual');
    var disclaimer = root.querySelector('.wm-it__disclaimer');

    hide([eyebrow, heading, text, media].filter(Boolean));
    if (buttons.length) gsap.set(buttons, { y: 16 });
    else if (actions && visible(actions)) gsap.set(actions, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'it');
    if (eyebrow) reveal(tl, eyebrow, 0, { duration: 0.45 });
    if (heading) reveal(tl, heading, 0.05);
    if (text) reveal(tl, text, 0.12, { duration: 0.55 });
    if (buttons.length) {
      tl.to(buttons, { y: 0, duration: 0.5, stagger: 0.06, ease: EASE }, 0.18);
    } else if (actions && visible(actions)) {
      tl.to(actions, { y: 0, duration: 0.5, ease: EASE }, 0.18);
    }
    if (media) reveal(tl, media, 0.1, { duration: 0.7 });
    if (disclaimer && visible(disclaimer)) revealRiseOnly(tl, disclaimer, 0.28);
  }

  function animateProductHero(sectionEl, root) {
    var media = root.querySelector('.wm-ph__gallery');
    var info = root.querySelector('.wm-ph__info');
    var purchase = root.querySelector('.wm-ph__purchase');
    var accordionItems = onlyVisible(qsa(root, '.wm-ph__accordion .wm-accordion__item'));
    var trust = root.querySelector('.wm-ph__trust');
    var nav = root.querySelector('.wm-ph__nav');

    hide([media, info, purchase, trust, nav].concat(accordionItems).filter(Boolean));

    var tl = makeScrollTimeline(sectionEl, 'ph');
    var t = 0;
    if (media) {
      reveal(tl, media, t, { duration: 0.65 });
      t += 0.08;
    }
    if (info) {
      reveal(tl, info, t);
      t += 0.08;
    }
    if (purchase) {
      reveal(tl, purchase, t, { duration: 0.6 });
      t += 0.08;
    }
    if (nav && visible(nav)) {
      reveal(tl, nav, 0.12, { duration: 0.5 });
    }
    if (trust && visible(trust)) {
      reveal(tl, trust, 0.16, { duration: 0.5 });
    }
    if (accordionItems.length) {
      reveal(tl, accordionItems, t, { stagger: 0.08, duration: 0.55 });
    }
  }

  function animateResults(sectionEl, root) {
    var media = root.querySelector('.wm-results__media');
    var heading = root.querySelector('.wm-results__heading--desktop, .wm-results__heading');
    var headingMobile = root.querySelector('.wm-results__heading--mobile');
    var text = root.querySelector('.wm-results__text');
    var btn = root.querySelector('.wm-results__btn');
    var refs = onlyVisible(qsa(root, '.wm-results__refs-item'));

    var headings = onlyVisible([heading, headingMobile].filter(Boolean));
    hide([media, text].concat(headings).concat(refs).filter(Boolean));
    if (btn && visible(btn)) gsap.set(btn, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'results');
    var t = 0;
    if (headings.length) {
      reveal(tl, headings, t, { stagger: 0.04, duration: 0.7 });
      t += 0.08;
    }
    if (text) {
      reveal(tl, text, t, { duration: 0.55 });
      t += 0.06;
    }
    if (btn && visible(btn)) {
      tl.to(btn, { y: 0, duration: 0.5, ease: EASE }, t);
      t += 0.06;
    }
    if (media) {
      reveal(tl, media, 0.08, { duration: 0.7 });
    }
    if (refs.length) {
      reveal(tl, refs, t, { stagger: 0.08, duration: 0.55 });
    }
  }

  function animateTimeline(sectionEl, root) {
    var heading = root.querySelector('.wm-tl__heading');
    var subtext = root.querySelector('.wm-tl__subtext');
    var tabs = onlyVisible(qsa(root, '.wm-tl__tab'));
    var activePanel = root.querySelector('.wm-tl__panel.is-active') || root.querySelector('.wm-tl__panel');
    var media = activePanel ? activePanel.querySelector('.wm-tl__media') : null;
    var progress = activePanel ? activePanel.querySelector('.wm-tl__progress') : null;
    var body = activePanel ? activePanel.querySelector('.wm-tl__body') : null;
    var btn = activePanel ? activePanel.querySelector('.wm-tl__btn') : null;

    hide([heading, subtext, media, progress, body].concat(tabs).filter(Boolean));
    if (btn && visible(btn)) gsap.set(btn, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'timeline');
    var t = 0;
    if (heading) {
      reveal(tl, heading, t, { duration: 0.7 });
      t += 0.06;
    }
    if (subtext) {
      reveal(tl, subtext, t, { duration: 0.55 });
      t += 0.06;
    }
    if (tabs.length) {
      reveal(tl, tabs, t, { stagger: 0.05, duration: 0.5 });
      t += 0.08;
    }
    if (media) {
      reveal(tl, media, t, { duration: 0.7 });
    }
    if (progress) {
      reveal(tl, progress, t + 0.06, { duration: 0.5 });
    }
    if (body) {
      reveal(tl, body, t + 0.1, { duration: 0.55 });
    }
    if (btn && visible(btn)) {
      tl.to(btn, { y: 0, duration: 0.5, ease: EASE }, t + 0.16);
    }
  }

  function animateMedicalReviewer(sectionEl, root) {
    var media = root.querySelector('.wm-mrev__media');
    var heading = root.querySelector('.wm-mrev__heading');
    var role = root.querySelector('.wm-mrev__role');
    var credential = root.querySelector('.wm-mrev__credential');
    var copy = root.querySelector('.wm-mrev__copy');

    hide([media, heading, role, credential].filter(Boolean));

    var tl = makeScrollTimeline(sectionEl, 'mrev');
    if (media) reveal(tl, media, 0, { duration: 0.6 });
    if (heading) reveal(tl, heading, 0.08, { duration: 0.5 });
    if (role) reveal(tl, role, 0.14, { duration: 0.5 });
    if (credential) reveal(tl, credential, 0.2, { duration: 0.5 });
    if (!heading && !role && !credential && copy) {
      hide([copy].filter(Boolean));
      reveal(tl, copy, 0.08, { duration: 0.55 });
    }
  }

  function animateNewsletter(sectionEl, root) {
    var heading = root.querySelector('.wm-nl__heading');
    var text = root.querySelector('.wm-nl__text:not(.wm-nl__text--mobile)');
    var textMobile = root.querySelector('.wm-nl__text--mobile');
    var fields = root.querySelector('.wm-nl__fields');
    var submit = root.querySelector('.wm-nl__submit');
    var legal = root.querySelector('.wm-nl__legal');

    var texts = onlyVisible([text, textMobile].filter(Boolean));
    hide([heading, fields, legal].concat(texts).filter(Boolean));
    if (submit && visible(submit)) gsap.set(submit, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'newsletter');
    var t = 0;
    if (heading) {
      reveal(tl, heading, t, { duration: 0.7 });
      t += 0.08;
    }
    if (texts.length) {
      reveal(tl, texts, t, { stagger: 0.04, duration: 0.55 });
      t += 0.08;
    }
    if (fields) {
      reveal(tl, fields, t, { duration: 0.55 });
      t += 0.06;
    }
    if (submit && visible(submit)) {
      tl.to(submit, { y: 0, duration: 0.5, ease: EASE }, t);
      t += 0.06;
    }
    if (legal) {
      reveal(tl, legal, t, { duration: 0.5 });
    }
  }

  function animateRichText(sectionEl, root) {
    // Animate real content nodes (heading text + each RTE child), not wrapper boxes.
    // Anything already on screen plays immediately; the rest reveals as it enters.
    var pageHeading = root.querySelector('.wm-rt__heading');
    var subtitle = root.querySelector('.wm-rt__subtitle');
    var blockHeadings = onlyVisible(qsa(root, '.wm-rt__block-heading-text'));
    var rteParts = onlyVisible(
      unique(
        []
          .concat(qsa(root, '.wm-rt__rte > *'))
          .concat(qsa(root, '.wm-rt__table-scroll'))
      ).filter(function (el) {
        if (el.tagName === 'TABLE' && el.closest('.wm-rt__table-scroll')) return false;
        return true;
      })
    );

    var units = onlyVisible(
      unique(
        []
          .concat(pageHeading ? [pageHeading] : [])
          .concat(subtitle ? [subtitle] : [])
          .concat(blockHeadings)
          .concat(rteParts)
      )
    );
    if (!units.length) return;

    hide(units);

    function playBatch(batch) {
      if (!batch || !batch.length) return;
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        duration: 0.55,
        stagger: 0.06,
        ease: EASE,
        overwrite: 'auto',
      });
    }

    function intersectsViewport(el) {
      var rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    var onScreen = [];
    var below = [];
    units.forEach(function (el) {
      if (intersectsViewport(el)) onScreen.push(el);
      else below.push(el);
    });

    // Fill the first screen right away — no blank holes waiting for scroll
    playBatch(onScreen);

    if (below.length) {
      ScrollTrigger.batch(below, {
        start: 'top 92%',
        once: true,
        onEnter: playBatch,
        id: (sectionEl.id || 'wm') + '-rich-text',
      });
    }
  }

  function animateHowToUse(sectionEl, root) {
    var media = root.querySelector('.wm-htu__media');
    var heading = root.querySelector('.wm-htu__heading');
    var text = root.querySelector('.wm-htu__text');
    var steps = onlyVisible(qsa(root, '.wm-htu__step'));
    hide([media, heading, text].concat(steps).filter(Boolean));
    var tl = makeScrollTimeline(sectionEl, 'htu');
    if (media) reveal(tl, media, 0);
    if (heading) reveal(tl, heading, 0);
    if (text) reveal(tl, text, 0.05, { duration: 0.55 });
    if (steps.length) reveal(tl, steps, 0.08, { stagger: 0.06 });
  }

  function animateSteps(sectionEl, root) {
    var eyebrow = root.querySelector('.wm-steps__eyebrow');
    var heading = root.querySelector('.wm-steps__heading');
    var text = root.querySelector('.wm-steps__text');
    var btn = root.querySelector('.wm-steps__btn');
    var items = onlyVisible(qsa(root, '.wm-steps__item'));

    hide([eyebrow, heading, text].concat(items).filter(Boolean));
    if (btn && visible(btn)) gsap.set(btn, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'steps');
    if (eyebrow) reveal(tl, eyebrow, 0, { duration: 0.4 });
    if (heading) reveal(tl, heading, 0.05);
    if (text) reveal(tl, text, 0.1, { duration: 0.5 });
    if (btn && visible(btn)) tl.to(btn, { y: 0, duration: 0.5, ease: EASE }, 0.14);
    if (items.length) reveal(tl, items, 0.18, { stagger: 0.1, duration: 0.7 });
  }

  function animateBmi(sectionEl, root) {
    var heading = root.querySelector('.wm-bmi-section__heading');
    var text = root.querySelector('.wm-bmi-section__text');
    var btn = root.querySelector('.wm-bmi-section__btn');
    var card = root.querySelector('.wm-bmi, [data-wm-bmi]');

    hide([heading, text].filter(Boolean));
    if (card) gsap.set(card, { y: 28 });
    if (btn && visible(btn)) gsap.set(btn, { y: 16 });

    var tl = makeScrollTimeline(sectionEl, 'bmi');
    if (heading) reveal(tl, heading, 0);
    if (text) reveal(tl, text, 0.08, { duration: 0.55 });
    if (btn && visible(btn)) tl.to(btn, { y: 0, duration: 0.5, ease: EASE }, 0.14);
    if (card && visible(card)) tl.to(card, { y: 0, duration: 0.7, ease: EASE }, 0.1);
  }

  function animateSection(sectionEl) {
    if (!sectionEl || sectionEl.getAttribute('data-wm-gsap') === '1') return;
    if (SKIP.test(sectionEl.className || '')) return;
    if (sectionEl.querySelector('.wm-form-header')) return;

    var root = sectionRoot(sectionEl);
    if (!root) return;

    sectionEl.setAttribute('data-wm-gsap', '1');

    if (sectionEl.classList.contains('section-molecule-hero') || root.classList.contains('wm-hero')) {
      animateHero(root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-ppc-hero') ||
      root.classList.contains('wm-ppc-hero')
    ) {
      animatePpcHero(root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-begin-consultation') ||
      root.classList.contains('wm-bc')
    ) {
      animateBeginConsultation(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-ppc-why-choose') ||
      root.classList.contains('wm-ppc-why')
    ) {
      animatePpcWhy(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-product-hero') ||
      root.classList.contains('wm-ph')
    ) {
      animateProductHero(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-results') ||
      root.classList.contains('wm-results')
    ) {
      animateResults(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-timeline') ||
      root.classList.contains('wm-tl')
    ) {
      animateTimeline(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-medical-reviewer') ||
      root.classList.contains('wm-mrev')
    ) {
      animateMedicalReviewer(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-newsletter') ||
      root.classList.contains('wm-nl')
    ) {
      animateNewsletter(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-rich-text') ||
      root.classList.contains('wm-rt')
    ) {
      animateRichText(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-image-text') ||
      root.classList.contains('wm-it')
    ) {
      animateImageText(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-steps') ||
      root.classList.contains('wm-steps')
    ) {
      animateSteps(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-bmi') ||
      root.classList.contains('wm-bmi-section') ||
      root.classList.contains('wm-bmi')
    ) {
      animateBmi(sectionEl, root);
      return;
    }
    if (
      sectionEl.classList.contains('section-molecule-how-to-use') ||
      root.classList.contains('wm-htu')
    ) {
      animateHowToUse(sectionEl, root);
      return;
    }

    animateScrollSection(sectionEl, root);
  }

  function initAll(scope) {
    if (reducedMotion()) return;

    var root = scope || document;
    var sections = unique(
      []
        .concat(qsa(root, '.shopify-section[class*="section-molecule-"]'))
        .concat(qsa(root, '.shopify-section[class*="section-wm-"]'))
    );

    sections.forEach(animateSection);
  }

  whenReady(function () {
    initAll(document);

    // One refresh after layout settles — avoid repeated refreshes (they cause glitches)
    window.addEventListener(
      'load',
      function () {
        ScrollTrigger.refresh();
      },
      { once: true }
    );

    document.addEventListener('shopify:section:load', function (event) {
      if (!event || !event.target) return;
      if (event.target.removeAttribute) event.target.removeAttribute('data-wm-gsap');
      qsa(event.target, '[data-wm-gsap]').forEach(function (el) {
        el.removeAttribute('data-wm-gsap');
      });
      initAll(event.target);
      ScrollTrigger.refresh();
    });
  });
})();
